import type { SubscriptionTier } from "./tiers";
import { recordKeys } from "../validation/narrow";

export type BillingInterval = "monthly" | "annual";

export type SellableTier = "pro";

export type PlanKey = "pro-monthly" | "pro-annual";

export type StripePriceEnvKey =
  | "STRIPE_PRICE_PRO_MONTHLY"
  | "STRIPE_PRICE_PRO_ANNUAL"
  | "STRIPE_PRICE_STARTER_MONTHLY"
  | "STRIPE_PRICE_STARTER_ANNUAL";

export interface PlanDefinition {
  planKey: PlanKey;
  tier: SellableTier;
  interval: BillingInterval;
  monthlyPrice: number;
  annualPrice: number;
  trialDays: number;
  stripeEnvKey: StripePriceEnvKey;
}

export const ANNUAL_DISCOUNT = 0.17;

function calculateAnnualPrice(monthlyPrice: number): number {
  return Math.round(monthlyPrice * 12 * (1 - ANNUAL_DISCOUNT));
}

const PRO_MONTHLY_PRICE = 99;

export const SUBSCRIPTION_PLANS: Record<PlanKey, PlanDefinition> = {
  "pro-monthly": {
    planKey: "pro-monthly",
    tier: "pro",
    interval: "monthly",
    monthlyPrice: PRO_MONTHLY_PRICE,
    annualPrice: calculateAnnualPrice(PRO_MONTHLY_PRICE),
    trialDays: 14,
    stripeEnvKey: "STRIPE_PRICE_PRO_MONTHLY",
  },
  "pro-annual": {
    planKey: "pro-annual",
    tier: "pro",
    interval: "annual",
    monthlyPrice: PRO_MONTHLY_PRICE,
    annualPrice: calculateAnnualPrice(PRO_MONTHLY_PRICE),
    trialDays: 14,
    stripeEnvKey: "STRIPE_PRICE_PRO_ANNUAL",
  },
} as const;

export const RETIRED_TIER_MONTHLY_PRICES: Partial<
  Record<SubscriptionTier, number>
> = {
  starter: 24.99,
};

export const RETIRED_TIER_PRICE_ENV_KEYS: ReadonlyArray<{
  tier: SubscriptionTier;
  envKeys: readonly StripePriceEnvKey[];
}> = [
  {
    tier: "starter",
    envKeys: ["STRIPE_PRICE_STARTER_MONTHLY", "STRIPE_PRICE_STARTER_ANNUAL"],
  },
];

export const ALL_PLAN_KEYS: PlanKey[] = recordKeys(SUBSCRIPTION_PLANS);

export function isValidPlanKey(key: string): key is PlanKey {
  return Object.keys(SUBSCRIPTION_PLANS).includes(key);
}

export function getPlan(planKey: PlanKey): PlanDefinition {
  return SUBSCRIPTION_PLANS[planKey];
}

export function getUnifiedPlans(): PlanDefinition[] {
  return ALL_PLAN_KEYS.map((key) => SUBSCRIPTION_PLANS[key]);
}

export function getEffectiveMonthlyPrice(plan: PlanDefinition): number {
  if (plan.interval === "annual") {
    return Math.round((plan.annualPrice / 12) * 100) / 100;
  }
  return plan.monthlyPrice;
}

export function getEffectiveAnnualMonthlyPrice(monthlyPrice: number): number {
  return Math.round((calculateAnnualPrice(monthlyPrice) / 12) * 100) / 100;
}

export function getAnnualSavings(monthlyPrice: number): number {
  const annualPrice = calculateAnnualPrice(monthlyPrice);
  return Math.round((monthlyPrice * 12 - annualPrice) * 100) / 100;
}

export interface TierPricing {
  monthlyPrice: number;
  annualPrice: number;
  effectiveMonthlyPrice: number;
}

export function getTierPricing(
  tier: SubscriptionTier,
  interval: BillingInterval,
): TierPricing | null {
  const monthlyPrice =
    tier === "pro"
      ? SUBSCRIPTION_PLANS["pro-monthly"].monthlyPrice
      : RETIRED_TIER_MONTHLY_PRICES[tier];
  if (monthlyPrice === undefined) return null;
  const annualPrice = calculateAnnualPrice(monthlyPrice);
  return {
    monthlyPrice,
    annualPrice,
    effectiveMonthlyPrice:
      interval === "annual"
        ? getEffectiveAnnualMonthlyPrice(monthlyPrice)
        : monthlyPrice,
  };
}

export interface ParsedPlanKey {
  tier: SellableTier;
  interval: BillingInterval;
}

export function parsePlanKey(planKey: string): ParsedPlanKey | null {
  if (!isValidPlanKey(planKey)) {
    return null;
  }

  const plan = SUBSCRIPTION_PLANS[planKey];
  return {
    tier: plan.tier,
    interval: plan.interval,
  };
}

export function buildPlanKey<TInterval extends BillingInterval>(
  tier: SellableTier,
  interval: TInterval,
): `${SellableTier}-${TInterval}` {
  return `${tier}-${interval}`;
}
